import React, { useEffect, useState } from "react";
import api from "../services/api";

// --- CSV export helper
function toCSV(rows) {
  if (!rows.length) return "";
  const headers = Object.keys(rows[0]);
  const escape = str => `"${String(str || "").replace(/"/g, '""')}"`;
  return [
    headers.join(","),
    ...rows.map(r => headers.map(h => escape(r[h])).join(","))
  ].join("\r\n");
}

// --- Main page
export default function AdminFeedbackPage() {
  const [feedbackList, setFeedbackList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [search, setSearch] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);

  // Modal state
  const [commentModal, setCommentModal] = useState({ open: false, feedback: null, comment: "" });

  // For copying to clipboard
  const [copySuccess, setCopySuccess] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/feedback?status=${statusFilter}&search=${search}`)
      .then(res => setFeedbackList(res.data))
      .finally(() => setLoading(false));
  }, [statusFilter, search, refresh]);

  // --- Sorting
  const sortedList = [...feedbackList].sort((a, b) => {
    let valA = a[sortBy], valB = b[sortBy];
    if (sortBy === "created_at") {
      valA = new Date(valA); valB = new Date(valB);
    }
    if (valA < valB) return sortDir === "asc" ? -1 : 1;
    if (valA > valB) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  // --- Pagination
  const pageCount = Math.ceil(sortedList.length / perPage);
  const pagedList = sortedList.slice((page - 1) * perPage, page * perPage);

  // --- Actions
  const handleMarkReviewed = (id) => {
    api.post(`/admin/feedback/${id}/comment`, { status: "reviewed" })
      .then(() => setRefresh(r => r + 1));
  };
  const handleDeleteFeedback = id => {
    if (window.confirm("Are you sure you want to permanently delete this feedback?")) {
      api.delete(`/admin/feedback/${id}`).then(() => setRefresh(r => r + 1));
    }
  };
  const handleEditComment = fb => {
    setCommentModal({ open: true, feedback: fb, comment: fb.admin_comment || "" });
  };
  const handleSaveComment = () => {
    api.post(`/admin/feedback/${commentModal.feedback.feedback_id}/comment`, { admin_comment: commentModal.comment })
      .then(() => {
        setCommentModal({ open: false, feedback: null, comment: "" });
        setRefresh(r => r + 1);
      });
  };
  const handleCopyFeedback = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopySuccess("Copied!");
      setTimeout(() => setCopySuccess(""), 900);
    });
  };
  const handleExportCSV = () => {
    const csv = toCSV(sortedList);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `user_feedback_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // --- Table column sort handler
  const handleSort = col => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
  };

  // --- Color helpers
  const statusColor = s =>
    s === "pending" ? "#fbe38c" :
    s === "reviewed" ? "#d0f7d1" : "#f1f3f7";

  // --- User hover info (expandable for future)
  const renderUser = fb => (
    <span
      title={`ID: ${fb.user_id}\nUsername: ${fb.username}`}
      style={{ textDecoration: "underline dotted", cursor: "pointer" }}
    >
      {fb.username || fb.user_id}
    </span>
  );

  // --- Quick filter chips
  const quickFilters = [
    { label: "Pending", value: "pending" },
    { label: "Reviewed", value: "reviewed" },
    { label: "All", value: "" }
  ];

  return (
    <div className="admin-container" style={{ maxWidth: 1200 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
        <h1 style={{ flex: 1 }}>User Feedback</h1>
        <button
          style={{
            background: "#e6f2ff", color: "#2174d4", border: "1px solid #b8dbff",
            borderRadius: 7, padding: "8px 16px", fontWeight: 600, marginRight: 10, cursor: "pointer"
          }}
          onClick={handleExportCSV}
        >
          Export CSV
        </button>
        <span style={{
          background: "#e37d4d", color: "#fff", fontWeight: 700, borderRadius: 10,
          padding: "6px 13px", fontSize: 16, marginLeft: 8, display: statusFilter === "pending" ? "inline-block" : "none"
        }}>
          {feedbackList.filter(f => f.status === "pending").length} Pending
        </span>
      </div>

      {/* Quick filter chips */}
      <div style={{ marginBottom: 12 }}>
        {quickFilters.map(f =>
          <button
            key={f.value}
            onClick={() => { setStatusFilter(f.value); setPage(1); }}
            style={{
              background: statusFilter === f.value ? "#1974d4" : "#f2f7fa",
              color: statusFilter === f.value ? "#fff" : "#226",
              border: "none",
              borderRadius: 13,
              padding: "6px 15px",
              fontWeight: 500,
              marginRight: 8,
              cursor: "pointer"
            }}
          >
            {f.label}
          </button>
        )}
        <input
          type="text"
          placeholder="Search user or feedback..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          style={{
            marginLeft: 18,
            padding: 7,
            minWidth: 210,
            borderRadius: 7,
            border: "1px solid #e2e4ef"
          }}
        />
      </div>
      {loading ? <div>Loading...</div> : (
        <div style={{ overflowX: "auto" }}>
          <table className="reports-table" style={{ minWidth: 980 }}>
            <thead>
              <tr>
                <th style={{ cursor: "pointer" }} onClick={() => handleSort("created_at")}>Date {sortBy === "created_at" && (sortDir === "asc" ? "▲" : "▼")}</th>
                <th style={{ cursor: "pointer" }} onClick={() => handleSort("username")}>User {sortBy === "username" && (sortDir === "asc" ? "▲" : "▼")}</th>
                <th>Type</th>
                <th>Feedback</th>
                <th>Status</th>
                <th>Admin Comment</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {pagedList.length === 0 ? (
                <tr><td colSpan={7}>No feedback found.</td></tr>
              ) : pagedList.map(fb => (
                <tr key={fb.feedback_id}>
                  <td>{new Date(fb.created_at).toLocaleString()}</td>
                  <td>{renderUser(fb)}</td>
                  <td>{fb.feedback_type}</td>
                  <td>
                    <span style={{ wordBreak: "break-word", maxWidth: 260, display: "inline-block" }}>
                      {fb.feedback_text}
                    </span>
                    <button
                      title="Copy feedback"
                      style={{ marginLeft: 8, fontSize: 15, background: "none", border: "none", cursor: "pointer" }}
                      onClick={() => handleCopyFeedback(fb.feedback_text)}
                    >📋</button>
                    {copySuccess && <span style={{ color: "#0a6d21", marginLeft: 4, fontWeight: 600 }}>{copySuccess}</span>}
                  </td>
                  <td>
                    <span style={{
                      background: statusColor(fb.status),
                      borderRadius: 10,
                      padding: "5px 12px",
                      fontWeight: 500,
                      color: "#443",
                      fontSize: 15
                    }}>
                      {fb.status}
                    </span>
                  </td>
                  <td>{fb.admin_comment || ""}</td>
                  <td>
                    {fb.status !== "reviewed" && (
                      <button
                        style={{ marginRight: 6, background: "#dbf7e8", color: "#118c3c", border: "none", borderRadius: 7, padding: "5px 12px", cursor: "pointer" }}
                        onClick={() => handleMarkReviewed(fb.feedback_id)}
                      >
                        Mark Reviewed
                      </button>
                    )}
                    <button
                      style={{ marginRight: 6, background: "#eef4fb", color: "#236", border: "none", borderRadius: 7, padding: "5px 12px", cursor: "pointer" }}
                      onClick={() => handleEditComment(fb)}
                    >
                      Comment
                    </button>
                    <button
                      style={{ color: "#c02b2b", border: "none", borderRadius: 7, padding: "5px 12px", background: "#fff1f1", cursor: "pointer" }}
                      onClick={() => handleDeleteFeedback(fb.feedback_id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div style={{ marginTop: 13, textAlign: "right" }}>
        {Array.from({ length: pageCount }, (_, i) => i + 1).map(pg => (
          <button
            key={pg}
            style={{
              marginLeft: 4,
              border: "none",
              borderRadius: 6,
              background: page === pg ? "#2574e4" : "#eee",
              color: page === pg ? "#fff" : "#333",
              padding: "6px 14px",
              fontWeight: 600,
              cursor: "pointer"
            }}
            onClick={() => setPage(pg)}
          >{pg}</button>
        ))}
      </div>

      {/* Admin comment modal */}
      {commentModal.open && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(44,44,44,0.16)", zIndex: 30, display: "flex", alignItems: "center", justifyContent: "center"
        }}>
          <div style={{
            background: "#fff", borderRadius: 11, boxShadow: "0 4px 32px rgba(44,64,90,0.22)", padding: 28, minWidth: 320, maxWidth: 400
          }}>
            <h3 style={{ margin: 0, marginBottom: 14 }}>Admin Comment</h3>
            <textarea
              rows={4}
              style={{ width: "100%", borderRadius: 7, border: "1px solid #e2e6f7", padding: 9, marginBottom: 13 }}
              value={commentModal.comment}
              onChange={e => setCommentModal(m => ({ ...m, comment: e.target.value }))}
              placeholder="Leave a note for future admins (or for reply-to-user in future)..."
            />
            <div style={{ textAlign: "right" }}>
              <button
                onClick={() => setCommentModal({ open: false, feedback: null, comment: "" })}
                style={{ marginRight: 12, background: "#eee", border: "none", borderRadius: 7, padding: "6px 14px", fontWeight: 500, cursor: "pointer" }}
              >Cancel</button>
              <button
                onClick={handleSaveComment}
                style={{ background: "#2574e4", color: "#fff", border: "none", borderRadius: 7, padding: "6px 16px", fontWeight: 600, cursor: "pointer" }}
              >Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
